import { num } from './utils';

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const MESES_RE = 'janeiro|fevereiro|marco|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro';

const norm = (s) => String(s || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const capMes = (nome) => {
  const n = String(nome || '');
  if (!n) return '';
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
};

const fmtValor = (v) => new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(num(v));

const escapeRe = (s) => String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const numMesRec = (rec) => {
  if (rec?.rec_parc != null && String(rec.rec_parc).trim() !== '') {
    const n = Number(rec.rec_parc);
    if (Number.isFinite(n)) return n;
  }
  const ep = String(rec?.rec_ep || '').match(/bm\s*(\d+)/i);
  if (ep) return Number(ep[1]);
  return null;
};

/**
 * rec_desc é o que a pessoa libera no faturamento.
 * Ex. AENA: "Mês 11. Setembro/2026." — competência do BM, não a data limite.
 * Ex. PO por nota: "Mês 3. Março/2026. PO 123456. ITM 78."
 */
export function extrairSlotsDaDescRec(desc) {
  const t = String(desc || '').trim();
  const out = {};
  if (!t) return out;

  const mMes = t.match(/m[eê]s\s+(\d{1,2})\b/i);
  if (mMes) out.numMes = Number(mMes[1]);

  const mNome = t.match(new RegExp(`\\b(${MESES_RE})\\s*/\\s*(\\d{4})\\b`, 'i'));
  if (mNome) {
    const idx = MESES.findIndex((m) => norm(m) === norm(mNome[1]));
    const nome = idx >= 0 ? MESES[idx] : mNome[1];
    out.mesAno = `${capMes(nome)}/${mNome[2]}`;
  } else {
    const mNomeDe = t.match(new RegExp(`\\b(${MESES_RE})\\s+(?:de\\s+)?(\\d{4})\\b`, 'i'));
    if (mNomeDe) {
      const idx = MESES.findIndex((m) => norm(m) === norm(mNomeDe[1]));
      const nome = idx >= 0 ? MESES[idx] : mNomeDe[1];
      out.mesAno = `${capMes(nome)}/${mNomeDe[2]}`;
    } else {
      const mNum = t.match(/\b(\d{1,2})\s*\/\s*(\d{4})\b/);
      if (mNum) {
        const mm = Number(mNum[1]);
        if (mm >= 1 && mm <= 12) out.mesAno = `${String(mm).padStart(2, '0')}/${mNum[2]}`;
      }
    }
  }

  const mVlr = t.match(/R\$\s*([\d.]+,\d{2})/i);
  if (mVlr) out.valor = mVlr[1];

  const mPo = t.match(/\b(?:PO|OC)\s*[:#=.-]?\s*([A-Za-z0-9][A-Za-z0-9./-]{2,})\b/i)
    || t.match(/ordem de compra\s*[:#=.-]?\s*([A-Za-z0-9][A-Za-z0-9./-]{2,})\b/i)
    || t.match(/pedido de compra\s*[:#=.-]?\s*([A-Za-z0-9][A-Za-z0-9./-]{2,})\b/i)
    || t.match(/\bPedido\s*[:#=.-]?\s*([A-Za-z0-9][A-Za-z0-9./-]{2,})\b/i);
  if (mPo) out.po = mPo[1];

  const mItm = t.match(/\bITM\s*[:#=.-]?\s*([A-Za-z0-9][A-Za-z0-9./-]{1,})\b/i);
  if (mItm) out.itm = mItm[1];

  const mFrs = t.match(/\bFRS\s*[:#=.-]?\s*([A-Za-z0-9][A-Za-z0-9./-]{1,})\b/i);
  if (mFrs) out.frs = mFrs[1];

  return out;
}

const valorCampoRec = (rec, label) => {
  const col = String(label || '').trim();
  if (!/^rec_[A-Za-z][A-Za-z0-9_]*$/.test(col)) return '';
  const v = rec?.[col];
  if (v == null || String(v).trim() === '') return '';
  if (/^rec_vlr/i.test(col)) return fmtValor(v);
  return String(v).trim();
};

const extrairRotulo = (desc, label) => {
  const raw = String(label || '').trim();
  if (!raw) return '';
  const t = String(desc || '');
  const re = new RegExp(`(?:^|[\\s.;,])${escapeRe(raw)}\\s*[:#=.–-]?\\s*([^\\s.;,}{]+)`, 'i');
  const m = t.match(re);
  return m ? String(m[1]).trim() : '';
};

const slotKind = (label) => {
  const n = norm(label);
  if (!n) return 'unknown';
  if (n.includes('valor') && (n.includes('bruto') || n.includes('medicao'))) return 'valor';
  if ((n.includes('num') || n.includes('numero') || n.includes('nro')) && n.includes('mes')) return 'numMes';
  if ((n.includes('mes') && n.includes('ano')) || n.includes('mes/ano')) return 'mesAno';
  if (n === 'mes' || n === 'mes do bm') return 'mesAno';
  if (
    n === 'po'
    || n === 'oc'
    || n === 'pedido'
    || /\b(po|oc)\b/.test(n)
    || n.includes('ordem de compra')
    || n.includes('pedido de compra')
  ) return 'po';
  if (n === 'frs' || /\bfrs\b/.test(n)) return 'frs';
  if (n === 'itm' || /\bitm\b/.test(n)) return 'itm';
  return 'unknown';
};

const detectNumMesPad = (samples) => {
  const vals = samples.map((s) => String(s || '').trim()).filter(Boolean);
  if (vals.length && vals.every((v) => /^\d{2}$/.test(v))) return 2;
  return 0;
};

const extractSlots = (template, filled) => {
  const keys = [...String(template).matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
  if (!keys.length) return [];
  const parts = String(template).split(/\{[^}]+\}/);
  let rest = String(filled || '');
  const prefix = parts[0] || '';
  if (prefix) {
    const i = rest.indexOf(prefix);
    if (i < 0) return [];
    rest = rest.slice(i + prefix.length);
  }
  const out = [];
  for (let i = 0; i < keys.length; i += 1) {
    const suffix = parts[i + 1] || '';
    if (!suffix) {
      out.push({ key: keys[i], value: rest });
      rest = '';
      continue;
    }
    const j = rest.indexOf(suffix);
    if (j < 0) return [];
    out.push({ key: keys[i], value: rest.slice(0, j) });
    rest = rest.slice(j + suffix.length);
  }
  return out;
};

const historicoUtil = (template, recs, recId) => {
  const tpl = String(template || '');
  if (!tpl.includes('{')) return [];
  return (Array.isArray(recs) ? recs : [])
    .filter((r) => Number(r?.rec_id) !== Number(recId))
    .map((r) => String(r?.rec_NFDescricao || '').trim())
    .filter((d) => d && !d.includes('{'))
    .map((d) => extractSlots(tpl, d))
    .filter((slots) => slots.length);
};

const chavesDoTemplate = (template) => [...String(template || '').matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);

/**
 * Lê o processo de faturamento (texto livre) e o template { } para saber
 * o que esta nota precisa trazer em rec_desc. Sem LLM: só convenção explícita.
 *
 * PO/ITM só são exigidos se:
 * - a chave existe no template, ou
 * - o processo fala em nova PO/ITM por nota, ou
 * - o processo diz que rec_desc precisa trazer PO/ITM
 */
export function requisitosDoProcesso(fatProc, template) {
  const proc = norm(fatProc);
  const keys = chavesDoTemplate(template);
  const req = [];

  const keyPo = keys.some((k) => slotKind(k) === 'po');
  const keyFrs = keys.some((k) => slotKind(k) === 'frs');
  const keyItm = keys.some((k) => slotKind(k) === 'itm');

  const recDescChunk = (proc.match(/rec_desc[^.\n]{0,100}/) || [''])[0];
  const recDescPedePo = /\b(po|oc|ordem de compra|pedido de compra)\b/.test(recDescChunk);
  const recDescPedeItm = /\bitm\b/.test(recDescChunk);

  const poPorNota = /nova (ordem|po|oc)|ordem de compra.{0,50}(cada|por) (nota|nf|emiss)|(?:po|oc).{0,24}(por|a cada|em cada|para cada) (nota|nf)/.test(proc);
  const itmPorNota = /novo itm|itm.{0,24}(por|a cada|em cada|para cada) (nota|nf)/.test(proc);

  if (keyPo || poPorNota || recDescPedePo) req.push({ id: 'po', label: 'ordem de compra (PO)' });
  if (keyFrs) req.push({ id: 'frs', label: 'FRS' });
  if (keyItm || itmPorNota || recDescPedeItm) req.push({ id: 'itm', label: 'ITM' });

  return req;
}

/**
 * Troca `{...}` do template da NF.
 * 1) rec_desc (o que foi escrito na liberação) — competência e PO/ITM só daqui
 * 2) formato das notas anteriores do mesmo contrato (padding / estilo)
 * 3) rec_parc / rec_vlrBru para número do mês e valor
 *
 * Não usa rec_dtNfPrev para {mês/ano do BM}: a data limite não é a competência.
 * Não inventa PO/ITM. Chave sem dado permanece `{...}`.
 */
export function preencherTemplateNfDescricao(template, rec, historico = []) {
  const tpl = String(template || '');
  if (!tpl.includes('{')) return tpl;

  const daDesc = extrairSlotsDaDescRec(rec?.rec_desc);
  const parsed = historicoUtil(tpl, historico, rec?.rec_id);
  const samplesNumMes = [];
  parsed.forEach((slots) => {
    slots.forEach((s) => {
      if (slotKind(s.key) === 'numMes') samplesNumMes.push(s.value);
    });
  });

  const padMes = detectNumMesPad(samplesNumMes);
  const nMes = daDesc.numMes != null ? daDesc.numMes : numMesRec(rec);

  return tpl.replace(/\{([^}]+)\}/g, (full, label) => {
    const kind = slotKind(label);
    if (kind === 'valor') return daDesc.valor || (rec?.rec_vlrBru != null && rec?.rec_vlrBru !== '' ? fmtValor(rec.rec_vlrBru) : full);
    if (kind === 'numMes') {
      if (nMes == null || !Number.isFinite(nMes)) return full;
      const s = String(nMes);
      return padMes > 0 ? s.padStart(padMes, '0') : s;
    }
    if (kind === 'mesAno') return daDesc.mesAno || full;
    if (kind === 'po') return daDesc.po || full;
    if (kind === 'frs') return daDesc.frs || full;
    if (kind === 'itm') return daDesc.itm || full;
    const doCampo = valorCampoRec(rec, label);
    if (doCampo) return doCampo;
    return extrairRotulo(rec?.rec_desc, label) || full;
  });
}

export function templateNfTemChaves(texto) {
  return /\{[^}]+\}/.test(String(texto || ''));
}

export function chavesPendentes(texto) {
  return [...String(texto || '').matchAll(/\{([^}]+)\}/g)].map((m) => `{${m[1]}}`);
}

/**
 * Guardrail da emissão: casa rec_desc com o processo do projeto.
 * Avisa / bloqueia. Nunca inventa.
 */
export function avaliarGuardrailNf({ rec, nfDescricao, fatProc, template } = {}) {
  const descNf = String(nfDescricao || rec?.rec_NFDescricao || '');
  const descRec = String(rec?.rec_desc || '');
  const fonte = `${descRec}\n${descNf}`;
  const slots = extrairSlotsDaDescRec(fonte);
  const req = requisitosDoProcesso(fatProc, template);
  const missing = [];
  const avisos = [];

  const leftover = chavesPendentes(descNf).filter((chave) => {
    const label = chave.slice(1, -1);
    return !valorCampoRec(rec, label);
  });
  if (leftover.length) {
    avisos.push(`Não preencho chave sem dado na descrição da receita (${leftover.join(', ')}).`);
  }

  req.forEach((r) => {
    if (r.id === 'po' && !slots.po) {
      missing.push('ordem de compra (PO) desta nota — indique na descrição da receita');
    }
    if (r.id === 'itm' && !slots.itm) {
      missing.push('ITM desta nota — indique na descrição da receita');
    }
  });

  if (!descRec.trim() && (templateNfTemChaves(template) || leftover.length)) {
    avisos.push('Descrição da receita vazia: número do mês e valor podem vir da parcela; competência e PO não.');
  }

  return {
    missing,
    avisos,
    extras: { po: slots.po || '', itm: slots.itm || '' },
  };
}
